import { useCallback, useEffect, useState } from "react";
import { getAlignmentResults } from "actions/alignmentResults";
import { NumberParam, useQueryParams } from "use-query-params";
import { useDispatch, useSelector } from "react-redux";
import { ConfidenceType, IDraggableAlignmentResult, IState, ModalDialog } from "store/types";
import { Sorted } from "./Sorted";
import { Unsorted } from "./Unsorted";
import { Exclude } from "./Exclude";
import { Button, Grid } from "@mui/material";
import { DragDropContext, DraggableLocation } from "@hello-pangea/dnd";
import { reorder, move, exclude, moveAll, excludeAll, canBeDraggedToThisPosition } from "lib/dragDropUtils";
import { isEmpty } from "lodash";
import styled from "@emotion/styled";
import { getCurrentSubjectName } from "lib/currentSubject";
import WestIcon from "@mui/icons-material/West";
import { useNavigate } from "@reach/router";
import { updateSubject } from "actions/subjects";
import { SubmitConfirmationDialog } from "./SubmitConfirmationDialog";
import { CancelConfirmationDialog } from "./CancelConfirmationDialog";
import { RulesResultsDialog } from "./RulesResultsDialog";
import { AlignmentScore } from "../../components/content/AlignmentScore";
import { requiredEnvVar } from "lib/envUtil";
import { RulesExecutionPage } from "./RulesExecutionPage";
import { mapFromDraggable, mapToDraggable } from "lib/mapper";
import { Shortcut } from "components/Layout";
import { hasUnsortedContent } from "lib/utils";
import { SubmitErrorDialog } from "./SubmitErrorDialog";
import { addSubjectUpdateId } from "actions/subjects";
import { InactiveRuleVersion } from "components/content/InactiveRuleVersion";
import { getConfig } from "actions/config";

const SiteTitle = styled.p`
  font-family: "Roboto";
  font-style: "normal";
  font-weight: 400;
  font-size: 31px;
  line-height: 31px;
  padding-left: 8px;
  margin: 0px;
  color: #252525;
`;

const SubjectTitle = styled.p`
  font-family: "Roboto";
  font-style: "normal";
  font-weight: 400;
  font-size: 16px;
  line-height: 16px;
  padding-top: 4px;
  padding-left: 8px;
  padding-bottom: 2px;
  margin: 0px;
  color: #414141;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 64px);
  background-color: white;
`;

const ContentPanel = styled.div`
  flex: 1 0 auto;
`;

const Footer = styled.footer`
  width: 100%;
  padding-top: 20px;
  padding-bottom: 20px;
  border-top: 1px solid black;
  flex-shrink: 0;
  margin-top: 0px;
  position: fixed;
  bottom: 0px;
`;

export const AlignmentToolPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Query parameters
  const [query, _setQuery] = useQueryParams({
    subjectId: NumberParam,
    templateId: NumberParam,
    ruleId: NumberParam,
  });

  // Load data from Moodle
  useEffect(() => {
    dispatch(getConfig());
    dispatch(getAlignmentResults(query.subjectId!, query.templateId!, query.ruleId));
  }, [dispatch, query.subjectId, query.templateId, query.ruleId]);

  // What we need from Redux
  const config = useSelector((state: IState) => state.config);
  const alignmentResults = useSelector((state: IState) => state.alignmentResults);
  const loading = useSelector((state: IState) => state.loadingConfig || state.loadingAlignmentResults);
  const subjectName = getCurrentSubjectName(query.subjectId);

  const [draggableContent, setDraggableContent] = useState<IDraggableAlignmentResult>();
  useEffect(() => {
    if (!loading && !isEmpty(alignmentResults)) {
      setDraggableContent(mapToDraggable(alignmentResults));
    }
  }, [alignmentResults, loading]);

  const onMoveResourceViaMenu = useCallback(
    (source: DraggableLocation, destination: DraggableLocation, draggableId: string[]) => {
      if (!isEmpty(draggableContent)) {
        if (draggableId.length === 1) {
          setDraggableContent(move(source, destination, draggableId[0], draggableContent));
        } else {
          setDraggableContent(moveAll(source, destination, draggableId, draggableContent));
        }
      }
    },
    [draggableContent],
  );

  const onExcludeResourceViaMenu = useCallback(
    (source: DraggableLocation, draggableId: string[]) => {
      if (!isEmpty(draggableContent)) {
        if (draggableId.length === 1) {
          setDraggableContent(exclude(source, draggableId[0], draggableContent));
        } else {
          setDraggableContent(excludeAll(source, draggableId, draggableContent));
        }
      }
    },
    [draggableContent],
  );

  const onDragEnd = useCallback(
    (result: any) => {
      const { destination, source, draggableId } = result;
      if (!destination || (destination.droppableId === source.droppableId && destination.index === source.index)) {
        return;
      }

      if (isEmpty(draggableContent)) {
        return;
      }

      if (source.droppableId === destination.droppableId) {
        // Reorder within same section
        let stagedData = reorder(source, destination, draggableId, draggableContent);
        if (!canBeDraggedToThisPosition(destination, stagedData)) {
          // If the intended drop position is not allowed, instead drop at the end of the section
          stagedData = reorder(
            source,
            { droppableId: destination.droppableId, index: 1000 },
            draggableId,
            draggableContent,
          );
        }
        setDraggableContent(stagedData);
      } else {
        // Move to a different section
        let stagedData = move(source, destination, draggableId, draggableContent);
        if (!canBeDraggedToThisPosition(destination, stagedData)) {
          // If the intended drop position is not allowed, instead drop at the end of the section
          stagedData = move(
            source,
            { droppableId: destination.droppableId, index: 1000 },
            draggableId,
            draggableContent,
          );
        }
        setDraggableContent(stagedData);
      }
    },
    [draggableContent],
  );

  const [openModal, setOpenModal] = useState(ModalDialog.RESULTS);
  const handleCloseModal = useCallback(() => setOpenModal(ModalDialog.NONE), []);

  // Back button functions
  const onBackButtonClick = useCallback(() => setOpenModal(ModalDialog.CANCEL), []);
  const handleCancelChanges = useCallback(
    () =>
      query.ruleId
        ? navigate(`/wizardStepOne?subjectId=${query.subjectId}&templateId=${query.templateId}&ruleId=${query.ruleId}`)
        : navigate(`/wizardStepOne?subjectId=${query.subjectId}&templateId=${query.templateId}`),
    [navigate, query.subjectId, query.templateId, query.ruleId],
  );

  // Submit button functions
  const onSubmitButtonClick = useCallback(() => setOpenModal(ModalDialog.SUBMIT), []);
  const handleSaveChanges = useCallback(async () => {
    handleCloseModal();
    const payload = mapFromDraggable(query.subjectId!, query.templateId!, draggableContent!);
    const response: any = await dispatch(updateSubject(payload));
    if (response && response.value && response.value.updateId) {
      // Add updateId to redux so we can poll its status
      dispatch(addSubjectUpdateId(response.value.updateId));
    }
    navigate(`/wizardStepThree?subjectId=${query.subjectId}&templateId=${query.templateId}`);
  }, [dispatch, draggableContent, handleCloseModal, navigate, query.subjectId, query.templateId]);

  const showConfidence = requiredEnvVar("SHOW_CONFIDENCE");

  // Track window height changes
  const [height, setHeight] = useState(window.innerHeight);
  const updateHeight = useCallback(() => {
    const offset = query.ruleId && config.currentVersion !== query.ruleId ? 190 : 160;
    setHeight(window.innerHeight - offset);
  }, [config.currentVersion, query.ruleId]);
  useEffect(() => {
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, [updateHeight]);

  if (loading) {
    return <RulesExecutionPage />;
  } else
    return (
      <Container>
        {showConfidence === "true" && (
          <div style={{ flexShrink: "0" }}>
            {/* TODO - where to get the score from?? */}
            <AlignmentScore score={ConfidenceType.MEDIUM} />
          </div>
        )}
        {query.ruleId && config.currentVersion !== query.ruleId && (
          <div style={{ flexShrink: "0" }}>
            <InactiveRuleVersion version={query.ruleId} />
          </div>
        )}
        <ContentPanel>
          <DragDropContext onDragEnd={onDragEnd}>
            <Grid container>
              <Grid
                item
                xs={4}
                style={{
                  paddingTop: "20px",
                  backgroundColor: "#EEEEEE",
                  overflowY: "auto",
                  maxHeight: height,
                  height: showConfidence === "true" ? height - 29 : height,
                }}
              >
                <Unsorted
                  state={draggableContent}
                  onMoveResourceViaMenu={onMoveResourceViaMenu}
                  onExcludeResourceViaMenu={onExcludeResourceViaMenu}
                />
                <Exclude state={draggableContent} onMoveResourceViaMenu={onMoveResourceViaMenu} />
              </Grid>
              <Grid
                item
                xs={8}
                style={{ paddingLeft: "18px", paddingRight: "18px", overflowY: "auto", maxHeight: height }}
              >
                <Grid container style={{ paddingTop: "36px" }}>
                  <Grid item xs={11} style={{ display: "flex", justifyContent: "left", alignItems: "center" }}>
                    <SiteTitle>New site structure</SiteTitle>
                  </Grid>
                  <Grid item xs={1} style={{ display: "flex", justifyContent: "right", alignItems: "center" }}>
                    <Shortcut>S</Shortcut>
                  </Grid>
                </Grid>
                <SubjectTitle>{subjectName}</SubjectTitle>
                <Sorted
                  state={draggableContent}
                  onMoveResourceViaMenu={onMoveResourceViaMenu}
                  onExcludeResourceViaMenu={onExcludeResourceViaMenu}
                />
              </Grid>
            </Grid>
          </DragDropContext>
        </ContentPanel>
        <Footer>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={10}>
              <div style={{ paddingLeft: "16px" }}>
                <Button variant="outlined" startIcon={<WestIcon />} onClick={onBackButtonClick}>
                  Back
                </Button>
              </div>
            </Grid>
            <Grid item xs={2} style={{ display: "flex", justifyContent: "right", alignItems: "right" }}>
              <div style={{ paddingRight: "16px" }}>
                <Button variant="contained" onClick={onSubmitButtonClick}>
                  Update site
                </Button>
              </div>
            </Grid>
          </Grid>
        </Footer>
        <RulesResultsDialog
          open={openModal === ModalDialog.RESULTS}
          onClose={handleCloseModal}
          state={draggableContent}
        />
        <CancelConfirmationDialog
          open={openModal === ModalDialog.CANCEL}
          onConfirm={handleCancelChanges}
          onClose={handleCloseModal}
        />
        <SubmitConfirmationDialog
          open={openModal === ModalDialog.SUBMIT}
          hasUnsortedContent={hasUnsortedContent(draggableContent)}
          onConfirm={handleSaveChanges}
          onClose={handleCloseModal}
        />
        <SubmitErrorDialog
          open={openModal === ModalDialog.ERRORS}
          onConfirm={handleSaveChanges}
          onClose={handleCloseModal}
        />
      </Container>
    );
};
